<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import HowWindowInsertsWork from '$lib/PageSections/HowWindowInsertsWork/HowWindowInsertsWork.svelte';

	import indowInsertsInstalledHistoricWindowImg from './images/indowInsertsInstalledHistoricWindow.jpg';
	import noiseReductionGuideImg from './images/noiseReductionGuide.jpg';
	import overwhelmingNoisePollutionImg from './images/overwhelmingNoisePollution.jpg';
</script>

<Section>
	<p>
		Our inserts help reduce noise in most situations, but we want to ensure we
		are the right solution for you. Every space is different. Many factors
		affect the noise reduction performance of your inserts. This guide will help
		you understand how Indow inserts impact noise entering a space.
	</p>
	<p>
		We’ve put this together based on customer feedback and third-party lab
		testing. This is a guide, not a guarantee.
	</p>
	<h2>How Noise Enters a Space</h2>
	<p>
		Sound waves enter your space through every surface, gap, and crack: walls,
		doors, ceilings, floors, vents, and windows.
	</p>
	<Image class="no-bleed" src={noiseReductionGuideImg} />
</Section>

<Section>
	<h2>What Noise Are You Dealing With?</h2>
	<ul
		class="
    max-w-prose list-none
    [&_h3]:text-left
    [&_li>p]:pl-4
  "
	>
		<li>
			<h3 class="text-indow-grey-dark normal-case">How close is it?</h3>
			<p>
				The closer the sound source, the stronger the sound waves. You may
				perceive a smaller reduction in noise from nearby sound sources.
			</p>
		</li>
		<li>
			<h3 class="text-indow-grey-dark normal-case">How high up are you?</h3>
			<p>
				Upper floors of high-rises experience noise as if the source is closer
				due to the reverberation between buildings. This may cause a reduction
				in the noise mitigation of our inserts.
			</p>
		</li>
		<li>
			<h3 class="text-indow-grey-dark normal-case">What is the frequency?</h3>
			<p>
				Our inserts are more effective at reducing sound at high frequencies
				than low frequencies. For example, Indow inserts will reduce
				high-frequency sounds of train screeching on the tracks far more than
				low-frequency vibrations of its rumbling engine.
			</p>
		</li>
	</ul>
</Section>

<Section backgroundColor="grey-light">
	<h2>How Much Noise Can Indow Inserts Reduce</h2>
	<p>
		The groupings below show from left to right how much noise damping you can
		expect from the addition of Indow inserts. Those on the left will most
		likely have the biggest change in the noise heard inside the space. The
		average STC rating for our window inserts installed in a window is 42. All
		windows have the potential to get to that rating, but the situations on the
		right may have started closer to that <a href="/resources/stc-rating"
			>STC rating</a
		> before installing Indow inserts.
	</p>

	<div
		class="
    mb-4 grid w-full grid-cols-1 place-items-center gap-2
    md:gap-0
    lg:grid-cols-[auto_1fr_auto]
  "
	>
		<span
			class="
     text-4xl text-indow-blue
     lg:rotate-180 lg:[writing-mode:vertical-rl]
   ">BEST</span
		>
		<div
			class="
     grid w-full grid-cols-1 place-items-center justify-between gap-4
     lg:grid-cols-[1fr_1fr_1fr]
   "
		>
			<!-- single pane -->
			<div
				class="
      xs:circle
      grid aspect-square w-full max-w-[16rem] grid-rows-[1fr_3fr]
      place-items-center justify-center gap-1 bg-indow-blue
      max-xs:p-2
    "
			>
				<h3 class="self-end text-indow-grey-dark!">SINGLE-PANE</h3>
				<div class="flex flex-col items-center gap-3 self-start">
					<p class="mb-0 text-sm">BIG WINDOWS</p>
					<p class="mb-0 text-sm">HIGHER FREQUENCIES</p>
					<p class="mb-0 text-sm">BUILDINGS WITH CONCRETE WALLS</p>
					<p class="mb-0 text-sm">3" OR MORE AIR GAP</p>
				</div>
			</div>
			<!-- double pane -->
			<div
				class="
      xs:circle
      grid aspect-square w-full max-w-[16rem] grid-rows-[1fr_3fr]
      place-items-center justify-center gap-1 bg-indow-blue
      max-xs:p-2
    "
			>
				<h3 class="self-end text-indow-grey-dark!">DOUBLE-PANE</h3>
				<div
					class="
       flex max-w-[90%] flex-col items-center gap-3 self-start
       md:gap-0
       lg:gap-3
     "
				>
					<p class="mb-0 p-0 text-sm">SMALLER WINDOWS</p>
					<p class="mb-0 p-0 text-sm">WIDE RANGE OF FREQUENCIES</p>
					<p class="mb-0 p-0 text-sm">
						STICK FRAME CONSTRUCTION
						<span class="p-0 text-xs">(STANDARD RESIDENTIAL CONSTRUCTION)</span>
					</p>
					<p class="mb-0 p-0 text-sm">2" OR LESS AIR GAP</p>
				</div>
			</div>
			<!-- triple pane -->
			<div
				class="
      xs:circle
      grid aspect-square w-full max-w-[16rem] grid-rows-[1fr_3fr] flex-col
      place-items-center justify-center gap-1 bg-indow-blue
      max-xs:p-2
    "
			>
				<h3 class="self-end text-indow-grey-dark!">TRIPLE-PANE</h3>
				<div class="flex flex-col items-center gap-3 self-start">
					<p class="mb-0 text-sm">LAMINATED GLASS</p>
					<p class="mb-0 text-sm">LOW BASS & VIBRATIONS</p>
					<p class="mb-0 text-sm">UNINSULATED OR HOLLOW WINDOW FRAMES</p>
				</div>
			</div>
		</div>
		<span
			class="
     text-4xl text-indow-blue
     lg:[writing-mode:vertical-rl]
   ">LEAST</span
		>
	</div>
	<p>
		Note: We do not recommend Indow inserts for reducing noise coming through
		laminated glass windows, triple-pane windows or hurricane windows.
	</p>
</Section>

<Section>
	<Image src={overwhelmingNoisePollutionImg} class="max-w-96" />
	<h2>What If My Inserts Do Not Reduce The Noise Enough?</h2>
	<Bubble backgroundColor="transparent">
		<ul>
			<li>
				Although Indow inserts may not provide as much of an overall noise
				reduction in the space as you are seeking, they are often one step on
				the path of a noise reduction strategy.
			</li>
			<li>
				To prevent an unsatisfactory install, make sure to fully evaluate your
				space and create an action plan that addresses your specific needs. Our
				Fit Specialists and Dealers can help with resources to get you started!
			</li>
			<li>
				Due to the custom nature of Indow inserts, all purchases are final and
				no refunds will be provided.
			</li>
		</ul>
	</Bubble>
	<Bubble backgroundColor="transparent" title="Best Practices">
		<ul>
			<li>Maximize space between insert and window.</li>
			<li>Address other sources of noise penetration.</li>
			<li>
				Treat all windows in a room with inserts. Partial treatment is unlikely
				to produce notable results.
			</li>
			<li>
				Be wary of apps on your phone for measuring noise levels (db). Use a
				consistent noise maker to trace entry points.
			</li>
		</ul>
	</Bubble>
	<Image class="no-bleed" src={indowInsertsInstalledHistoricWindowImg} />
</Section>
<Section backgroundColor="blue">
	<h2>What Noise Reduction Was Documented in Testing?</h2>
	<ul class="marker:text-indow-white">
		<li>
			Standard Grade inserts reduced sound coming through operable single-pane
			windows by 10 dBa (50%). Acoustic Grade reduced sound by 18.9 dBa (70%) –
			Independent analysis completed by Portland State University
		</li>
		<li>
			Acoustic Grade inserts reduced sound coming through operable double-pane
			by 10-12 dBa. (approx. 50%). This yields a 42-45 STC rating, reducing a
			sound like loud speech to a murmur, or entirely inaudible – Independent
			analysis completed by BCIT Centre for Architectural Ecology, Burnaby,
			British Columbia, Canada
		</li>
		<li>
			Acoustic Grade inserts combined with operable single-pane yields an STC
			rating of 39, where loud speech can sometimes be heard, but not clearly –
			Analysis completed by NWAA Acoustical Laboratories, Elma, Washington
		</li>
		<li>
			Please note the lab measured dBa reductions came from an isolated test
			window and do not measure overall noise reduction within a room. Overall
			noise reduction depends on the amount of noise entering through all
			surfaces and openings. Even small openings can have large impacts.
		</li>
	</ul>
</Section>
<HowWindowInsertsWork />
<GetAFreeEstimate />
